import { beginWrite, endWrite } from './updateGuard';

const BASE_URL = process.env.REACT_APP_API_URL;
const _cache = {};

// Persistent cache layer. Backs `_cache` to localStorage so when the OS
// kills the PWA tab (iOS does this aggressively for background tabs)
// and the user reopens the app, every tab paints instantly from the
// pre-kill cache instead of cold-fetching every endpoint. The
// stale-while-revalidate logic in apiCached then kicks in for any
// entries past TTL — instant paint, fresh data ~1s later.
// 🚨 NAMESPACED BY LOCATION. This cache is written to localStorage and hydrated
// on module load — i.e. on every page load, INCLUDING the reload that a shop
// switch performs. Under one shared key, switching to Albany would repaint up
// to 24h of Kingston's cached reads into the Albany session, which is exactly
// the data bleed the reload exists to prevent. One key per shop makes crossing
// over impossible rather than merely unlikely.
const PERSIST_SITE = (() => {
  try { const s = localStorage.getItem('3c_site'); return (s === 'albany' || s === 'kingston') ? s : 'kingston'; }
  catch (_) { return 'kingston'; }
})();
const PERSIST_KEY = '3c_api_cache_v1_' + PERSIST_SITE;

// 🚨 The in-memory key carries the shop too, read LIVE on every call rather
// than frozen at module load like PERSIST_SITE above.
//
// switchSite() reloads precisely so  cannot outlive a shop change,
// but a reload is not instant: between the flag flipping and the page
// actually going away, React can repaint with the NEW shop's label while
//  still holds the OLD shop's rows. That window was invisible while
// Albany had five customers and obvious once it had 3,008 — the list is what
// takes the time to render.
//
// With the shop in the key that window is closed by construction: the instant
// 3c_site flips, every lookup misses, so the worst case is an empty list for a
// moment rather than the other shop's. Isolation stops depending on reload
// timing. (RLS never leaked — a Kingston session provably sees zero Albany
// rows — so this is only ever about what the screen shows.)
function _cacheSite() {
  try { const s = localStorage.getItem('3c_site'); return (s === 'albany' || s === 'kingston') ? s : 'kingston'; }
  catch (_) { return 'kingston'; }
}
// The pre-multi-location key. Its contents are Kingston's, but nothing
// distinguishes them, so drop it once rather than risk it being read as either
// shop's. Cheap: it rebuilds on the next fetch.
try { localStorage.removeItem('3c_api_cache_v1'); } catch (_) {}
const PERSIST_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h hard cap
const PERSIST_MAX_BYTES  = 3 * 1024 * 1024;     // 3MB headroom under quota

(function _hydratePersistentCache() {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return;
    const now = Date.now();
    Object.keys(parsed).forEach((key) => {
      const e = parsed[key];
      if (!e || typeof e.ts !== 'number' || typeof e.data === 'undefined') return;
      if ((now - e.ts) > PERSIST_MAX_AGE_MS) return;
      _cache[key] = { data: e.data, ts: e.ts };
    });
  } catch (e) { /* corrupt cache — ignore, will rebuild on next write */ }
})();

// 🚨 Reads whose payload grows with the business must NEVER be written to
// localStorage. The customer list is ~2.3 MB at Albany, and persisting it
// cost far more than it saved: JSON.stringify plus a SYNCHRONOUS
// localStorage write of that, re-run every time any cached read landed, and
// a matching JSON.parse blocking module load on every boot. They stay in
// memory, where they are still a hit for the whole session.
const PERSIST_SKIP = ["getAllCustomers"];
// Backstop for the next unbounded read someone adds without thinking of this.
const PERSIST_MAX_ENTRY_BYTES = 512 * 1024;

let _persistTimer = null;
function _schedulePersist() {
  if (_persistTimer) return;
  _persistTimer = setTimeout(() => {
    _persistTimer = null;
    try {
      const now = Date.now();
      // Newest first, so a full budget drops the least-recently-written
      // entry rather than whichever happened to be enumerated last.
      const fresh = Object.keys(_cache)
        .filter((key) => {
          const e = _cache[key];
          if (!e || (now - e.ts) > PERSIST_MAX_AGE_MS) return false;
          // Skipped BEFORE serializing — the whole point is not to pay
          // stringify on a payload this size.
          return !PERSIST_SKIP.some((n) => key.indexOf(n) !== -1);
        })
        .sort((a, b) => _cache[b].ts - _cache[a].ts);
      // One stringify per entry, and the budget is measured as we go. The
      // old version re-stringified the ENTIRE snapshot once per entry it
      // dropped — quadratic work in exactly the case where the snapshot was
      // already too big to serialize cheaply.
      const parts = [];
      let used = 2; // the enclosing braces
      for (const key of fresh) {
        let piece;
        // A value that will not serialize must not take the snapshot with it.
        try { piece = JSON.stringify(_cache[key]); } catch (_) { continue; }
        if (!piece || piece.length > PERSIST_MAX_ENTRY_BYTES) continue;
        const k = JSON.stringify(key);
        const cost = k.length + 1 + piece.length + 1;
        if (used + cost > PERSIST_MAX_BYTES) continue;
        used += cost;
        parts.push(k + ":" + piece);
      }
      localStorage.setItem(PERSIST_KEY, "{" + parts.join(",") + "}");
    } catch (_) { /* quota exceeded — degrade silently */ }
  }, 500);
}

// Hydrate from any pre-warm response the hub stashed at PIN-verify time.
// One-shot: read once on module load, drop the sessionStorage entry so a
// subsequent SPA navigation goes through normal apiCached. The key shape
// must exactly match what apiCached builds (BASE_URL + JSON.stringify of
// the same params object — `{api, start, end}` insertion order matters).
(function _hydrateWarmCache() {
  try {
    const raw = sessionStorage.getItem('3c_warm_overview');
    if (!raw) return;
    sessionStorage.removeItem('3c_warm_overview');
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.params || !parsed.data) return;
    if (Date.now() - (parsed.ts || 0) > 300000) return; // 5min sanity bound
    const key = BASE_URL + JSON.stringify(parsed.params);
    _cache[key] = { data: parsed.data, ts: parsed.ts || Date.now() };
    // The hub stashes a getDashboardOverview response. That payload
    // also contains the data that getRevenueSummary + getBookingsRange
    // would return — seed those sibling caches too so Revenue / Sales
    // Tax / Books-Income / Schedule all paint from cache on first
    // mount post-login without an extra /exec call apiece.
    _maybeSeedSiblingCaches(parsed.params, parsed.data);
  } catch (e) {}
})();

// Read auth token at call time so logout takes effect immediately.
function _authToken() {
  try { return localStorage.getItem('3c_token') || ''; } catch { return ''; }
}

let _authFailing = false;
function _handleAuthFailure() {
  // Supabase-backend mode: the Supabase session is the source of truth, NOT the
  // legacy GAS token. Many endpoints are still GAS (all of 3CAM, plus a few
  // 3CHA reads), and GAS enforces a 1h sliding token TTL. When one of those
  // fires after the token lapsed it returns {authRequired}, but the user's
  // Supabase session is still perfectly valid — so we must NOT wipe local auth
  // and bounce to the PIN. Let that single call fail; the app stays logged in.
  // (Pre-Supabase, GAS was the only auth, so the wipe+redirect was correct.)
  if (process.env.REACT_APP_BACKEND === 'supabase') return;
  if (_authFailing) return;
  _authFailing = true;
  try {
    localStorage.removeItem('3c_role');
    localStorage.removeItem('3c_auth');
    localStorage.removeItem('3c_expiry');
    localStorage.removeItem('3c_token');
  } catch {}
  // Hard reload to force re-login. Skip if we're already at root.
  if (window.location.pathname !== '/') window.location.href = '/';
  else window.location.reload();
}

// 🚨 Apps Script has NO concept of location. Its /exec backend reads and writes
// ONE Google Sheet + one Drive folder — Kingston's — no matter who is calling.
// So any legacy call made from an Albany session is a cross-shop operation:
// searchInvoicePdfs would list Kingston's invoice PDFs, and dedupeStock would
// MUTATE Kingston's stock sheet.
//
// Guarding the two transports rather than the three surviving endpoints means
// this holds for anything still on, or later moved back onto, the legacy path.
// Everything Albany needs is already on Supabase (see the 2026-08-10 GAS audit),
// so refusing here costs that shop nothing.
function _assertKingston(what) {
  let s = 'kingston';
  try { s = localStorage.getItem('3c_site') || 'kingston'; } catch (_) {}
  if (s !== 'kingston') {
    throw new Error(
      'This action runs on the legacy Apps Script backend, which only knows the '
      + 'Kingston shop. Switch to 3C Kingston to use it. (' + what + ')'
    );
  }
}

async function api(params) {
  _assertKingston((params && (params.api || params.action)) || 'legacy read');
  const url = new URL(BASE_URL);
  Object.entries(params).forEach(([k,v]) => url.searchParams.set(k, String(v)));
  const token = _authToken();
  if (token) url.searchParams.set('token', token);
  const res = await fetch(url.toString(), { redirect: 'follow' });
  if (!res.ok) throw new Error('Network error: ' + res.status);
  const data = await res.json();
  if (data && data.authRequired) { _handleAuthFailure(); throw new Error('Session expired'); }
  return data;
}

async function post(body) {
  // Refuse BEFORE beginWrite() — a throw after it would leave the seamless-update
  // guard held open with no matching endWrite.
  _assertKingston((body && body.action) || 'legacy write');
  // Bracket every /exec write so the seamless-update controller won't reload
  // the page mid-mutation (see src/lib/updateGuard.js).
  beginWrite();
  try {
    const token = _authToken();
    const payload = { ...body, origin: '3cha-spa', ...(token ? { token } : {}) };
    const res = await fetch(BASE_URL, { method:'POST', headers:{'Content-Type':'text/plain'}, body:JSON.stringify(payload) });
    if (!res.ok) throw new Error('Network error: ' + res.status);
    const data = await res.json();
    if (data && data.authRequired) { _handleAuthFailure(); throw new Error('Session expired'); }
    return data;
  } finally {
    endWrite();
  }
}

// Per-key in-flight promise dedupe — two simultaneous callers for the
// same endpoint share a single network round-trip. Without this, the
// idle pre-warm + an immediate user tab click for the same data would
// both fire to /exec.
const _inflight = {};

// Canonical client-cache TTL — used by every apiCached read and every
// peekCache in the SPA. Set to 2 hours so within-session navigation is
// effectively instant; the only paths that refresh from /exec are:
//   • first page load (cache empty)
//   • hard refresh (Live button / pull-to-refresh / Ctrl+R)
//   • any mutation that calls bustClientCache on the relevant key
//   • returning to the Overview tab after the page was hidden
// Stale-while-revalidate kicks in between TTL and 2×TTL (4h here), so a
// long-running session still keeps painting instantly while a background
// refresh quietly updates the cache.
export const LONG_TTL_MS = 2 * 60 * 60 * 1000;

async function apiCached(params, ttlMs) {
  ttlMs = ttlMs || LONG_TTL_MS;
  const key = BASE_URL + '|' + _cacheSite() + '|' + JSON.stringify(params);
  const now = Date.now();
  const hit = _cache[key];
  if (hit && (now - hit.ts) < ttlMs) return hit.data;
  // Stale-while-revalidate: if the entry is past TTL but within 2x,
  // return the stale value immediately AND fire a background refresh.
  // The refresh updates _cache so the next mount paints fresh data.
  // Mount-time peekCache callers also benefit since they read _cache
  // directly. Capped at 2x TTL so genuinely-cold data still pays the
  // round-trip rather than serving day-old state.
  if (hit && (now - hit.ts) < (ttlMs * 2)) {
    if (!_inflight[key]) {
      _inflight[key] = api(params)
        .then(data => { _cache[key] = { data, ts: Date.now() }; _maybeSeedSiblingCaches(params, data); _schedulePersist(); return data; })
        .catch(() => hit.data)
        .finally(() => { delete _inflight[key]; });
    }
    return hit.data;
  }
  if (_inflight[key]) return _inflight[key];
  _inflight[key] = api(params)
    .then(data => { _cache[key] = { data, ts: Date.now() }; _maybeSeedSiblingCaches(params, data); _schedulePersist(); return data; })
    .finally(() => { delete _inflight[key]; });
  return _inflight[key];
}

// The same cache, for a read that is NOT a /exec call.
//
// apiCached is welded to api(params). The Supabase reads in apiSupabase.js
// re-export over their legacy namesakes, so getAllCustomers — cached for years
// as a /exec endpoint — silently became an UNCACHED direct query the moment it
// moved to Supabase. Nothing wrote its cache entry any more, so every
// peekCache() for an instant paint missed, and each of the seven call sites in
// Dashboard paid a full cold fetch of every customer and vehicle.
//
// Same key shape, same site namespacing, same stale-while-revalidate, same
// single-flight — so peekCache(params) finds it and the persisted snapshot
// paints instantly on the next load.
export async function cachedRead(params, ttlMs, fetcher) {
  ttlMs = ttlMs || LONG_TTL_MS;
  const key = BASE_URL + '|' + _cacheSite() + '|' + JSON.stringify(params);
  const now = Date.now();
  const hit = _cache[key];
  if (hit && (now - hit.ts) < ttlMs) return hit.data;
  const run = () => fetcher()
    .then((data) => { _cache[key] = { data, ts: Date.now() }; _schedulePersist(); return data; })
    .finally(() => { delete _inflight[key]; });
  if (hit && (now - hit.ts) < (ttlMs * 2)) {
    // Stale but usable: paint now, refresh behind it.
    if (!_inflight[key]) _inflight[key] = run().catch(() => hit.data);
    return hit.data;
  }
  // Single-flight: seven components mounting at once share ONE fetch instead
  // of firing seven identical full-table reads.
  if (_inflight[key]) return _inflight[key];
  _inflight[key] = run();
  return _inflight[key];
}

// Compound endpoints return data that other endpoints would also return.
// Seeding sibling caches turns one /exec response into multiple warm
// caches — saves the next mount of RevenueTab / SalesTaxTab /
// ScheduleTab from making its own round-trip.
function _maybeSeedSiblingCaches(params, data) {
  if (!data || data.success === false) return;
  try {
    if (params.api === 'getDashboardOverview') {
      if (data.revenueSummary) {
        const revKey = BASE_URL + JSON.stringify({ api: 'getRevenueSummary' });
        _cache[revKey] = { data: { success: true, summary: data.revenueSummary }, ts: Date.now() };
      }
      if (data.bookings && params.start && params.end) {
        const rangeKey = BASE_URL + JSON.stringify({ api: 'getBookingsRange', start: params.start, end: params.end });
        _cache[rangeKey] = { data: { success: true, bookings: data.bookings }, ts: Date.now() };
      }
    }
  } catch (_) {}
}

export function bustClientCache(keyMatch) {
  Object.keys(_cache).forEach(k => { if (!keyMatch || k.includes(keyMatch)) delete _cache[k]; });
  // Mirror busts into the persistent store so reopening the PWA after
  // a mutation doesn't paint the stale pre-mutation snapshot.
  _schedulePersist();
}

// Synchronously wipe the persisted snapshot. The normal persist path is
// debounced 500ms (_schedulePersist), which is fine for in-session
// mutations but loses the race against an immediate navigation: the
// "Live" hard-refresh button used to clear _cache then reload before the
// timer fired, so the stale localStorage snapshot survived and rehydrated
// on the next load — a brand-new customer/booking wouldn't appear until
// the 2h TTL lapsed. Call this before any forced reload so the page comes
// back with an empty cache and re-fetches everything live.
export function clearPersistentCache() {
  if (_persistTimer) { clearTimeout(_persistTimer); _persistTimer = null; }
  try { localStorage.removeItem(PERSIST_KEY); } catch (_) {}
}
// Sync read of the cache. Returns the cached value if it exists and is
// within ttlMs of its write timestamp; undefined otherwise. Lets a
// component hydrate from the cache on mount without paying the Promise
// microtask delay (and without flashing a skeleton on warm mounts).
export function peekCache(params, ttlMs) {
  const key = BASE_URL + '|' + _cacheSite() + '|' + JSON.stringify(params);
  const e = _cache[key];
  if (e && (Date.now() - e.ts) < (ttlMs || LONG_TTL_MS)) return e.data;
  return undefined;
}
// Convenience: any booking-affecting mutation should call this so
// both the per-range cache and the compound Overview cache reload.
function _bustBookingsCaches() {
  bustClientCache('getBookingsRange');
  bustClientCache('getBookings');
  bustClientCache('getDashboardOverview');
}
// TTL was 60s. Bumped to 5min — every booking mutation calls
// bustClientCache('getBookingsRange'), so within-session staleness is
// only possible from out-of-band edits (another user, sheet edit).
// Trades that tiny risk for instant tab-hops + warmer pre-warmed cache.
export async function getBookingsRange(startISO, endISO) { return apiCached({ api:'getBookingsRange', start:startISO, end:endISO }, LONG_TTL_MS); }
export async function getBlocks(startISO, endISO) { return apiCached({ api:'getBlocks', start:startISO||'', end:endISO||'' }, LONG_TTL_MS); }
export async function addBlock(date, bay, reason, createdBy, timeOfDay) { bustClientCache('getBlocks'); return post({ action:'addBlock', date, bay:bay||'', reason:reason||'', createdBy:createdBy||'', timeOfDay:timeOfDay||'' }); }
export async function removeBlock(id) { bustClientCache('getBlocks'); return post({ action:'removeBlock', id }); }
export async function getTimeOff(startISO, endISO) { return apiCached({ api:'getTimeOff', start:startISO||'', end:endISO||'' }, LONG_TTL_MS); }
export async function addTimeOff(date, role, reason, createdBy) { bustClientCache('getTimeOff'); return post({ action:'addTimeOff', date, role:role||'', reason:reason||'', createdBy:createdBy||'' }); }
export async function removeTimeOff(id) { bustClientCache('getTimeOff'); return post({ action:'removeTimeOff', id }); }
export async function verifyPin(role, pin) { return api({ api:'verifyPin', role, pin }); }
export async function verifySecretWord(role, word) { return api({ api:'verifyWord', role, word }); }
export async function getBookings(date) { return api({ api:'getBookings', filters:JSON.stringify({ date }) }); }
export async function updateBookingStatus(bookingId, status) { _bustBookingsCaches(); return post({ action:'updateBookingStatus', bookingId, status }); }
export async function deleteBooking(bookingId) { _bustBookingsCaches(); return post({ action:'deleteBooking', bookingId }); }
export async function updateBooking(bookingId, data) { _bustBookingsCaches(); return post({ action:'updateBooking', bookingId, data }); }
export async function addVehicle(customerId, data) { bustClientCache('getAllCustomers'); return api({ api:'addVehicle', customerId, data:JSON.stringify(data) }); }
export async function deleteVehicle(vehicleId) { bustClientCache('getAllCustomers'); return post({ action:'deleteVehicle', vehicleId }); }
export async function updateVehicle(vehicleId, data) { bustClientCache('getAllCustomers'); return post({ action:'updateVehicle', vehicleId, data }); }
export async function createBooking(data) { _bustBookingsCaches(); bustClientCache('getAllCustomers'); return post({ action:'createBooking', data }); }
export async function getBayConfig() { return apiCached({ api:'getBayConfig' }, LONG_TTL_MS); }
export async function getAllCustomers() { return apiCached({ api:'getAllCustomers' }, LONG_TTL_MS); }
export async function getRevenueSummary() { return apiCached({ api:'getRevenueSummary' }, LONG_TTL_MS); }
export async function getDashboardOverview(startISO, endISO) { return apiCached({ api:'getDashboardOverview', start:startISO, end:endISO }, LONG_TTL_MS); }
export async function getWeeklyGoal() { return apiCached({ api:'getWeeklyGoal' }, LONG_TTL_MS); }
export async function saveWeeklyGoal(goal, quote) { bustClientCache('getWeeklyGoal'); return post({ action:'saveWeeklyGoal', goal, quote }); }
export async function getGiftCards() { return apiCached({ api:'getGiftCards' }, LONG_TTL_MS); }
export async function updateGiftCardStatus(id, status) { bustClientCache('getGiftCards'); return post({ action:'updateGiftCardStatus', id, status }); }
export async function createGiftCard(data) { bustClientCache('getGiftCards'); return post({ action:'createGiftCard', data }); }
export async function updateGiftCard(data) { bustClientCache('getGiftCards'); return post({ action:'updateGiftCard', data }); }
export async function updateInvoiceRow(data) { bustClientCache('getRevenueSummary'); bustClientCache('getDashboardOverview'); return post({ action:'updateInvoiceRow', data }); }
export async function linkInvoiceToBooking(invoiceId, bookingId) { bustClientCache('getRevenueSummary'); bustClientCache('getDashboardOverview'); _bustBookingsCaches(); return post({ action:'linkInvoiceToBooking', invoiceId, bookingId }); }
export async function unlinkInvoiceFromBooking(invoiceId) { bustClientCache('getRevenueSummary'); bustClientCache('getDashboardOverview'); _bustBookingsCaches(); return post({ action:'unlinkInvoiceFromBooking', invoiceId }); }

// 3CAM expenses — 5-min TTL so tab-hops between Expenses / Bank
// Statements / Reports paint instantly from cache. Mutations
// (add/update/delete + bank-txn convert/link/unlink) all bust
// 'listExpenses' so within-session staleness only comes from
// out-of-band edits.
export async function listExpenses(filters) { return apiCached({ api:'listExpenses', filters: filters ? JSON.stringify(filters) : '' }, LONG_TTL_MS); }
export async function listExpenseVendors() { return apiCached({ api:'listExpenseVendors' }, LONG_TTL_MS); }
export async function addExpense(data) { bustClientCache('listExpenses'); bustClientCache('listExpenseVendors'); return post({ action:'addExpense', data }); }
export async function updateExpense(data) { bustClientCache('listExpenses'); bustClientCache('listExpenseVendors'); return post({ action:'updateExpense', data }); }
export async function deleteExpense(id) { bustClientCache('listExpenses'); bustClientCache('listExpenseVendors'); return post({ action:'deleteExpense', id }); }

// Vendors roll-up (QB-style vendor list aggregated from Expenses).
export async function listVendorsRollup(filters) {
  return apiCached({ api:'listVendorsRollup', filters: filters ? JSON.stringify(filters) : '' }, LONG_TTL_MS);
}

// Mileage tracking.
export async function listMileage(filters) {
  return apiCached({ api:'listMileage', filters: filters ? JSON.stringify(filters) : '' }, LONG_TTL_MS);
}
export async function addMileage(data) {
  bustClientCache('listMileage');
  return post({ action:'addMileage', data });
}
export async function updateMileage(data) {
  bustClientCache('listMileage');
  return post({ action:'updateMileage', data });
}
export async function deleteMileage(id) {
  bustClientCache('listMileage');
  return post({ action:'deleteMileage', id });
}
// Receipt OCR — uploads a base64 image, returns { vendor, date,
// amount, category, notes, dateGuessed, confidence }.
export async function parseReceiptImage(imageBase64, mimeType) {
  return post({ action:'parseReceiptImage', imageBase64, mimeType: mimeType || 'image/jpeg' });
}

// Recurring expense templates.
export async function listRecurringExpenses() {
  return apiCached({ api:'listRecurringExpenses' }, LONG_TTL_MS);
}
export async function addRecurringExpense(data) {
  bustClientCache('listRecurringExpenses');
  return post({ action:'addRecurringExpense', data });
}
export async function updateRecurringExpense(data) {
  bustClientCache('listRecurringExpenses');
  return post({ action:'updateRecurringExpense', data });
}
export async function deleteRecurringExpense(id) {
  bustClientCache('listRecurringExpenses');
  return post({ action:'deleteRecurringExpense', id });
}
export async function runDueRecurringExpenses() {
  bustClientCache('listRecurringExpenses');
  bustClientCache('listExpenses');
  return post({ action:'runDueRecurringExpenses' });
}

// Recurring SERVICES — auto-create bookings on a cadence
// (membership maintenance details, etc.). Mirrors recurring
// expenses but writes to Bookings via createBooking().
export async function listRecurringBookings() {
  return apiCached({ api:'listRecurringBookings' }, LONG_TTL_MS);
}
export async function addRecurringBooking(data) {
  bustClientCache('listRecurringBookings');
  return post({ action:'addRecurringBooking', data });
}
export async function updateRecurringBooking(data) {
  bustClientCache('listRecurringBookings');
  return post({ action:'updateRecurringBooking', data });
}
export async function deleteRecurringBooking(id) {
  bustClientCache('listRecurringBookings');
  return post({ action:'deleteRecurringBooking', id });
}
export async function runDueRecurringBookings() {
  bustClientCache('listRecurringBookings');
  bustClientCache('getBookingsRange');
  bustClientCache('getDashboardOverview');
  return post({ action:'runDueRecurringBookings' });
}
// Tasks — lightweight role-assigned to-dos. Cached 2h like the rest of
// the site; every mutation busts so within-session edits are instant.
export async function listTasks({ status, scope } = {}) {
  return apiCached({ api:'listTasks', status: status || '', scope: scope || '' }, LONG_TTL_MS);
}
export async function addTask(data) { bustClientCache('listTasks'); return post({ action:'addTask', data }); }
export async function updateTask(data) { bustClientCache('listTasks'); return post({ action:'updateTask', data }); }
export async function markTaskDone(id) { bustClientCache('listTasks'); return post({ action:'markTaskDone', id }); }
export async function reopenTask(id) { bustClientCache('listTasks'); return post({ action:'reopenTask', id }); }
export async function deleteTask(id) { bustClientCache('listTasks'); return post({ action:'deleteTask', id }); }
// Employees + timeclock (3CAM, owner-only).
export async function listEmployees() { return apiCached({ api:'listEmployees' }, LONG_TTL_MS); }
export async function addEmployee(data) { bustClientCache('listEmployees'); return post({ action:'addEmployee', data }); }
export async function updateEmployee(data) { bustClientCache('listEmployees'); return post({ action:'updateEmployee', data }); }
export async function deleteEmployee(id) { bustClientCache('listEmployees'); return post({ action:'deleteEmployee', id }); }
export async function getTimeclock(employeeId, weekOf) { return apiCached({ api:'getTimeclock', employeeId, weekOf }, LONG_TTL_MS); }
export async function listEmployeeWeeks(employeeId) { return apiCached({ api:'listEmployeeWeeks', employeeId }, LONG_TTL_MS); }
export async function getEmployeeArchive(employeeId) { return apiCached({ api:'getEmployeeArchive', employeeId }, LONG_TTL_MS); }
export async function saveTimeclock(data) { bustClientCache('getTimeclock'); bustClientCache('getLaborSummary'); bustClientCache('listEmployeeWeeks'); bustClientCache('getEmployeeArchive'); return post({ action:'saveTimeclock', data }); }
export async function getLaborSummary(year) { return apiCached({ api:'getLaborSummary', year: String(year||'') }, LONG_TTL_MS); }

// 3CAM bank statements (Phase 3) — Rondout Savings Bank PDFs.
// Parser stubbed until a real sample is in hand. Uploads + listing
// work today; parseBankStatement returns { parserNotImplemented:true }
// until parseRondoutPdf is filled in server-side.
export async function listBankStatements() { return apiCached({ api:'listBankStatements' }, LONG_TTL_MS); }
export async function listBankTransactions(statementId) { return apiCached({ api:'listBankTransactions', statementId }, LONG_TTL_MS); }
export async function uploadBankStatement(fileBase64, fileName, mimeType, notes, bankSource, onProgress) {
  bustClientCache('listBankStatements');
  var body = { action:'uploadBankStatement', fileBase64, fileName, mimeType: mimeType || 'application/pdf', notes: notes || '', bankSource: bankSource || 'rondout' };
  // When onProgress is supplied, use XHR so we can surface real upload
  // byte counts. Otherwise fall back to the standard fetch-based post.
  return typeof onProgress === 'function' ? _postWithProgress(body, onProgress) : post(body);
}

// XHR-based POST that drives onProgress(loaded, total, pct) so callers can
// show an upload bar. Mirrors `post()` otherwise: text/plain content type,
// token + origin folded in, authRequired handling on the response.
//
// ⚠️ We deliberately do NOT attach any listener to `xhr.upload`. Per the CORS
// spec, registering ANY xhr.upload listener makes a cross-origin request
// "non-simple" and forces a CORS *preflight* (OPTIONS) before the POST. Apps
// Script `/exec` has no doOptions and returns no CORS headers for OPTIONS, so
// the preflight fails and the real POST never fires — surfacing as a bare
// "Network error" even for a tiny payload with valid auth. (This is exactly
// why plain-`fetch` writes like addExpense work but this upload didn't.) Real
// byte-level upload progress is therefore impossible against Apps Script; we
// simulate a coarse bar that eases toward 90% and snaps to 100% on response.
function _postWithProgress(body, onProgress) {
  // Track as a write so an auto-update can't reload mid-upload.
  beginWrite();
  const p = new Promise((resolve, reject) => {
    const token = _authToken();
    const payload = { ...body, origin: '3cha-spa', ...(token ? { token } : {}) };
    const xhr = new XMLHttpRequest();
    xhr.open('POST', BASE_URL, true);
    xhr.setRequestHeader('Content-Type', 'text/plain');
    // Simulated progress — keeps the bar alive without an xhr.upload listener.
    let pct = 0;
    const tick = setInterval(() => {
      pct = Math.min(0.9, pct + 0.08 + Math.random() * 0.04);
      try { onProgress(pct, 1, pct); } catch (_) {}
    }, 200);
    const stopTick = () => { try { clearInterval(tick); } catch (_) {} };
    xhr.onload = () => {
      stopTick();
      try { onProgress(1, 1, 1); } catch (_) {}
      if (xhr.status < 200 || xhr.status >= 400) {
        return reject(new Error('Network error: ' + xhr.status));
      }
      let data;
      try { data = JSON.parse(xhr.responseText); }
      catch (e) { return reject(new Error('Bad response: ' + xhr.responseText.slice(0, 100))); }
      if (data && data.authRequired) { _handleAuthFailure(); return reject(new Error('Session expired')); }
      resolve(data);
    };
    xhr.onerror = () => { stopTick(); reject(new Error('Network error')); };
    xhr.ontimeout = () => { stopTick(); reject(new Error('Upload timed out')); };
    xhr.send(JSON.stringify(payload));
  });
  return p.finally(endWrite);
}
export async function parseBankStatement(statementId) {
  bustClientCache('listBankStatements');
  bustClientCache('listBankTransactions');
  bustClientCache('listExpenses');
  bustClientCache('getReconciliationSummary');
  return post({ action:'parseBankStatement', statementId });
}
export async function convertBankTxnToExpense(txnId, category, vendor, notes) {
  bustClientCache('listBankTransactions');
  bustClientCache('listExpenses');
  bustClientCache('getReconciliationSummary');
  return post({ action:'convertBankTxnToExpense', txnId, category, vendor: vendor || '', notes: notes || '' });
}
export async function setBankTxnIgnored(txnId, ignored) {
  bustClientCache('listBankTransactions');
  bustClientCache('getReconciliationSummary');
  return post({ action:'setBankTxnIgnored', txnId, ignored: !!ignored });
}
export async function deleteBankStatement(statementId) {
  bustClientCache('listBankStatements');
  bustClientCache('listBankTransactions');
  bustClientCache('getReconciliationSummary');
  return post({ action:'deleteBankStatement', statementId });
}
export async function setBankStatementSource(statementId, bankSource) {
  bustClientCache('listBankStatements');
  return post({ action:'setBankStatementSource', statementId, bankSource });
}

// 3CAM Phase 4 — reconciliation
export async function getReconciliationSummary() {
  return apiCached({ api:'getReconciliationSummary' }, LONG_TTL_MS);
}
export async function listUnmatchedBankTxns({ sinceDays, amount } = {}) {
  return api({ api:'listUnmatchedBankTxns', filters: JSON.stringify({ sinceDays: sinceDays || '', amount: (amount === undefined || amount === null) ? '' : amount }) });
}
export async function linkExpenseToBankTxn(expenseId, txnId) {
  bustClientCache('listExpenses');
  bustClientCache('listBankTransactions');
  bustClientCache('getReconciliationSummary');
  return post({ action:'linkExpenseToBankTxn', expenseId, txnId });
}
export async function unlinkExpenseFromBankTxn(expenseId) {
  bustClientCache('listExpenses');
  bustClientCache('listBankTransactions');
  bustClientCache('getReconciliationSummary');
  return post({ action:'unlinkExpenseFromBankTxn', expenseId });
}

// 3cMA — Marketing Agent
export async function getMarketingData() { return apiCached({ api:'getMarketingData' }, LONG_TTL_MS); }
export async function saveMarketingCategory(data) { bustClientCache('getMarketingData'); return post({ action:'saveMarketingCategory', data }); }
export async function deleteMarketingCategory(id) { bustClientCache('getMarketingData'); return post({ action:'deleteMarketingCategory', id }); }
export async function askMarketingAgent(question, history) { return api({ api:'askMarketingAgent', question, history: JSON.stringify((history||[]).slice(-6)) }); }

export async function getChecklist(bookingId) { return api({ api:'getChecklist', bookingId }); }
export async function amendChecklist(bookingId, service) { return post({ action:'amendChecklist', bookingId, service }); }
export async function tickTask(taskId, completedBy) { return api({ api:'tickTask', taskId, completedBy:completedBy||'detailer' }); }
export async function untickTask(taskId) { return api({ api:'untickTask', taskId }); }
export async function addCustomTask(bookingId, task, addedBy) { return post({ action:'addCustomTask', bookingId, task, addedBy:addedBy||'' }); }
export async function removeChecklistTask(taskId) { return post({ action:'removeChecklistTask', taskId }); }
export async function syncInvoices() {
  bustClientCache('getRevenueSummary');
  bustClientCache('getDashboardOverview');
  const url = new URL(BASE_URL);
  url.searchParams.set('api', 'syncInvoices');
  const token = _authToken();
  if (token) url.searchParams.set('token', token);
  const res = await fetch(url.toString(), { redirect: 'follow' });
  const text = await res.text();
  let data;
  try { data = JSON.parse(text); } catch(e) { throw new Error('Bad response: ' + text.slice(0,100)); }
  if (data && data.authRequired) { _handleAuthFailure(); throw new Error('Session expired'); }
  return data;
}
// Fast sync pre-check (Tier 1): 15 newest Drive filenames + parsed S.O.# +
// fileId, no OCR, no sheet read. The Supabase syncInvoices uses this to decide
// whether the heavy GAS import is even needed. Uncached — must be fresh.
export async function listRecentInvoiceFilenames() {
  return api({ api: 'listRecentInvoiceFilenames' });
}
export async function syncCustomers() {
  bustClientCache('getAllCustomers');
  const url = new URL(BASE_URL);
  url.searchParams.set('api', 'syncCustomers');
  const token = _authToken();
  if (token) url.searchParams.set('token', token);
  const res = await fetch(url.toString(), { redirect: 'follow' });
  const text = await res.text();
  let data;
  try { data = JSON.parse(text); } catch(e) { throw new Error('Bad response: ' + text.slice(0,100)); }
  if (data && data.authRequired) { _handleAuthFailure(); throw new Error('Session expired'); }
  return data;
}
export async function getNotifications(role) {
  return api({ api: 'getNotifications', role });
}

export async function dismissNotification(notifId, role) {
  bustClientCache('getNotifications');
  return api({ api: 'dismissNotification', notifId, role });
}

export async function listPendingBookingRequests() {
  return api({ api: 'listPendingBookingRequests' });
}

export async function approveBookingRequest(requestId, { bay, startTime, date, bypassConflict, service } = {}) {
  bustClientCache('listPendingBookingRequests');
  bustClientCache('getNotifications');
  _bustBookingsCaches();
  return post({
    action: 'approveBookingRequest',
    requestId,
    bay: bay || '',
    startTime: startTime || '',
    date: date || '',
    bypassConflict: !!bypassConflict,
    service: service || '',
  });
}
export async function rejectBookingRequest(requestId, reason) {
  bustClientCache('listPendingBookingRequests');
  bustClientCache('getNotifications');
  return post({ action: 'rejectBookingRequest', requestId, reason: reason || '' });
}
export async function getAnnualGoal() {
  return apiCached({ api: 'getAnnualGoal' }, LONG_TTL_MS);
}

export async function saveAnnualGoal(goal) {
  bustClientCache('getAnnualGoal');
  return post({ action: 'saveAnnualGoal', goal });
}

export async function getStock() { return apiCached({ api: 'getStock' }, LONG_TTL_MS); }
export async function updateStockLevel(stockId, qty) { bustClientCache('getStock'); return post({ action: 'updateStockLevel', stockId, qty }); }
export async function saveStockItem(data) { bustClientCache('getStock'); return post({ action: 'saveStockItem', data }); }
export async function deleteStockItem(stockId) { bustClientCache('getStock'); return api({ api: 'deleteStockItem', stockId }); }
export async function dedupeStock() { bustClientCache('getStock'); return post({ action: 'dedupeStock' }); }
export async function updateCustomer(customerId, data) { bustClientCache('getAllCustomers'); return api({ api:'updateCustomer', customerId, data:JSON.stringify(data) }); }
export async function deleteCustomer(customerId) { bustClientCache('getAllCustomers'); return post({ action:'deleteCustomer', customerId }); }
export async function cancelBooking(bookingId) { _bustBookingsCaches(); return post({ action:'cancelBooking', bookingId }); }
export async function requestDeposit(bookingId, amount) { _bustBookingsCaches(); return post({ action:'requestDeposit', bookingId, amount }); }
export async function markDepositPaid(bookingId) { _bustBookingsCaches(); return post({ action:'markDepositPaid', bookingId }); }
export async function markDepositRefunded(bookingId) { _bustBookingsCaches(); return post({ action:'markDepositRefunded', bookingId }); }
export async function sendDepositRequestEmail(bookingId) { _bustBookingsCaches(); return post({ action:'sendDepositRequestEmail', bookingId }); }
export async function requestBalance(bookingId) { _bustBookingsCaches(); return post({ action:'requestBalance', bookingId }); }
export async function markBalancePaid(bookingId, paidAmount, paymentRef) { _bustBookingsCaches(); return post({ action:'markBalancePaid', bookingId, paidAmount, paymentRef: paymentRef || 'manual' }); }
export async function sendBalanceRequestEmail(bookingId) { _bustBookingsCaches(); return post({ action:'sendBalanceRequestEmail', bookingId }); }
// Outbox tab and the badge poll hammer this — cache for 60s and bust
// on any draft mutation. The cross-ref path also calls with status=''
// so the cache covers all variants.
export async function getEmailDrafts(status) { return apiCached({ api:'getEmailDrafts', status: status || '' }, LONG_TTL_MS); }
function _bustEmailDraftsCache() { bustClientCache('getEmailDrafts'); }
export async function generatePreDraft(bookingId) { _bustEmailDraftsCache(); return post({ action:'generatePreDraft', bookingId }); }
export async function regeneratePreDraft(bookingId) { _bustEmailDraftsCache(); return post({ action:'regeneratePreDraft', bookingId }); }
export async function generatePostDraft(bookingId) { _bustEmailDraftsCache(); return post({ action:'generatePostDraft', bookingId }); }
export async function generateBalanceDraft(bookingId) { _bustEmailDraftsCache(); return post({ action:'generateBalanceDraft', bookingId }); }
export async function buildEodDrafts() { _bustEmailDraftsCache(); return post({ action:'buildEodDrafts' }); }
export async function updateEmailDraft(draftId, data) { _bustEmailDraftsCache(); return post({ action:'updateEmailDraft', draftId, ...data }); }
export async function sendEmailDraft(draftId) { _bustEmailDraftsCache(); return post({ action:'sendEmailDraft', draftId }); }
export async function deleteEmailDraft(draftId) { _bustEmailDraftsCache(); return post({ action:'deleteEmailDraft', draftId }); }
export async function createReplyDraft(bookingId, note, customerName) {
  _bustEmailDraftsCache();
  return post({ action:'createReplyDraft', bookingId, note: note || '', customerName: customerName || '' });
}
export async function createComebackDraft({ customerId, customerName, email, monthsSinceLastVisit }) {
  _bustEmailDraftsCache();
  return post({
    action:'createComebackDraft',
    customerId: customerId || '',
    customerName: customerName || '',
    email: email || '',
    monthsSinceLastVisit: monthsSinceLastVisit || 0,
  });
}
export async function searchInvoicePdfs(q) { return api({ api:'searchInvoicePdfs', q: q || '' }); }
export async function askOwnerAI(question, history) { return api({ api:'askOwnerAI', question, history: JSON.stringify((history||[]).slice(-6)) }); }
export async function setVipChecklistVisible(bookingId, visible) { _bustBookingsCaches(); return post({ action:'setVipChecklistVisible', bookingId, visible }); }
export async function getVipInbox() { return apiCached({ api:'getVipInbox' }, LONG_TTL_MS); }
export async function deleteVipNote(noteId) { bustClientCache('getVipInbox'); return post({ action:'deleteVipNote', noteId }); }
// Engineer modal data — scratchpad, maintenance state, leaderboards,
// heatmap, etc. Mostly static within a session, so cache aggressively
// (10 minutes). Every mutation below busts it so changes show up on
// the next open without a manual reload.
export async function getEngineerData() { return apiCached({ api:'getEngineerData' }, LONG_TTL_MS); }
export async function engineerSaveScratchpad(content) { bustClientCache('getEngineerData'); return post({ action:'engineerSaveScratchpad', content }); }
export async function engineerSavePrivateScratchpad(content) { bustClientCache('getEngineerData'); return post({ action:'engineerSavePrivateScratchpad', content }); }
export async function engineerMarkBugFixed() { bustClientCache('getEngineerData'); return post({ action:'engineerMarkBugFixed' }); }
export async function engineerSetMaintenanceMode(enabled, message, durationMinutes) { bustClientCache('getEngineerData'); return post({ action:'engineerSetMaintenanceMode', enabled, message, durationMinutes }); }
export async function engineerForceLogoutAll() { bustClientCache('getEngineerData'); return post({ action:'engineerForceLogoutAll' }); }
export async function engineerDeleteHeatmapCombo(combo) { bustClientCache('getAllCustomers'); bustClientCache('getEngineerData'); return post({ action:'engineerDeleteHeatmapCombo', combo }); }
export async function deleteInvoiceRow(invoiceId) {
  bustClientCache('getRevenueSummary');
  bustClientCache('getDashboardOverview');
  return post({ action:'deleteInvoiceRow', invoiceId });
}
// Insert-or-update a customer. customerId optional — if omitted, server
// matches by phone/name/email and creates a new row when no match.
// Returns {success, customerId, created}.
export async function upsertCustomer(data) {
  bustClientCache('getAllCustomers');
  return post({ action:'upsertCustomer', data });
}

export async function pushBookingToToday(bookingId) {
  const n = new Date();
  const today = n.getFullYear() + '-' + String(n.getMonth() + 1).padStart(2, '0') + '-' + String(n.getDate()).padStart(2, '0');
  // Sequential — the date update isn't legally a "status" transition
  // so it lands unconditionally; the status reset goes second and
  // surfaces a real error if the server still rejects (e.g. legacy
  // booking with an unrecognized status string). Previously errors
  // were swallowed via `await x; await y;` plus the caller's catch,
  // leaving the row date-updated but status-stale on transition
  // rejection — looked like "Push to Today did nothing" to staff.
  const dateResp = await updateBooking(bookingId, { date: today });
  if (dateResp && dateResp.success === false) {
    throw new Error('Date update failed: ' + (dateResp.error || 'unknown'));
  }
  const statusResp = await updateBookingStatus(bookingId, 'scheduled');
  if (statusResp && statusResp.success === false) {
    throw new Error('Status reset failed: ' + (statusResp.error || 'unknown'));
  }
  return { success: true };
}
